import React from 'react';
import DatePicker from 'react-datepicker';
import { CSVLink } from 'react-csv';
import {
  subDays,
  subMonths,
  subYears,
  startOfDay,
  startOfMonth,
  endOfMonth,
  differenceInCalendarDays,
  differenceInCalendarMonths,
} from 'date-fns';
import { ko } from 'date-fns/locale';

const CalendarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="5" width="18" height="16" rx="2" stroke="#9aa3af" strokeWidth="1.8" />
    <path d="M3 9h18M8 3v4M16 3v4" stroke="#9aa3af" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="2" />
    <path d="M16 16l5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const DateField = React.forwardRef(({ value, onClick }, ref) => (
  <button type="button" className="hourly-date-field" onClick={onClick} ref={ref}>
    <CalendarIcon />
    <span>{value || '날짜 선택'}</span>
  </button>
));

DateField.displayName = 'DateField';

const ReportToolbar = ({
  periodOptions,
  activePeriod,
  onPeriodClick,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  pickerMode = 'date',
  maxRangeDays,
  maxRangeMonths = 3,
  maxPickerDate = new Date(),
  minPickerDate,
  rangeHint,
  tempViewMode,
  setTempViewMode,
  tempDisplayType,
  setTempDisplayType,
  handleSearch,
  isLoading = false,
  csvData = [],
  csvFilename = 'report.csv',
  csvHeaders,
  onDateChange,
}) => {
  const isMonthMode = pickerMode === 'month';
  const dateFormat = isMonthMode ? 'yyyy-MM' : 'yyyy-MM-dd';
  const resolvedMinDate = minPickerDate ?? (isMonthMode ? subYears(maxPickerDate, 3) : undefined);

  const handleStartChange = (date) => {
    if (isMonthMode) {
      const start = startOfMonth(date);
      setStartDate(start);
      if (differenceInCalendarMonths(endDate, start) > maxRangeMonths - 1) {
        setEndDate(endOfMonth(start));
      } else if (start > endDate) {
        setEndDate(endOfMonth(start));
      }
    } else {
      setStartDate(date);
      if (maxRangeDays && differenceInCalendarDays(endDate, date) > maxRangeDays - 1) {
        setEndDate(date);
      } else if (
        !maxRangeDays &&
        maxRangeMonths &&
        differenceInCalendarMonths(endDate, date) > maxRangeMonths - 1
      ) {
        setEndDate(date);
      } else if (date > endDate) {
        setEndDate(date);
      }
    }
    onDateChange?.();
  };

  const handleEndChange = (date) => {
    if (isMonthMode) {
      const end = endOfMonth(date);
      setEndDate(end);
      if (differenceInCalendarMonths(end, startDate) > maxRangeMonths - 1) {
        setStartDate(startOfMonth(subMonths(end, maxRangeMonths - 1)));
      } else if (end < startDate) {
        setStartDate(startOfMonth(end));
      }
    } else {
      setEndDate(date);
      if (maxRangeDays && differenceInCalendarDays(date, startDate) > maxRangeDays - 1) {
        setStartDate(subDays(date, maxRangeDays - 1));
      } else if (
        !maxRangeDays &&
        maxRangeMonths &&
        differenceInCalendarMonths(date, startDate) > maxRangeMonths - 1
      ) {
        setStartDate(startOfDay(subMonths(date, maxRangeMonths - 1)));
      } else if (date < startDate) {
        setStartDate(date);
      }
    }
    onDateChange?.();
  };

  const startMinDate = isMonthMode
    ? resolvedMinDate
    : maxRangeDays
      ? subDays(endDate, maxRangeDays - 1)
      : maxRangeMonths
        ? startOfDay(subMonths(endDate, maxRangeMonths - 1))
        : resolvedMinDate;

  const startMaxDate = isMonthMode ? endDate : endDate;
  const endMinDate = isMonthMode ? startDate : startDate;
  const endMaxDate = isMonthMode
    ? maxPickerDate
    : maxPickerDate;

  const hasCsv = Array.isArray(csvData) && csvData.length > 0;

  return (
    <div className="hourly-toolbar-wrap">
      <div className="hourly-toolbar">
        <div className="hourly-toolbar__section">
          <span className="hourly-toolbar__label">기간 선택</span>
          <div className="hourly-pill-group">
            {periodOptions.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                className={`hourly-pill ${activePeriod === key ? 'active' : ''}`}
                onClick={() => onPeriodClick(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="hourly-toolbar__divider" aria-hidden="true" />

        <div className="hourly-toolbar__section hourly-toolbar__section--date">
          <span className="hourly-toolbar__label">기간 설정</span>
          <div className="hourly-date-range">
            <DatePicker
              selected={startDate}
              onChange={handleStartChange}
              dateFormat={dateFormat}
              showMonthYearPicker={isMonthMode}
              maxDate={startMaxDate}
              minDate={startMinDate}
              locale={ko}
              customInput={<DateField />}
            />
            <span className="hourly-date-tilde">~</span>
            <DatePicker
              selected={endDate}
              onChange={handleEndChange}
              dateFormat={dateFormat}
              showMonthYearPicker={isMonthMode}
              maxDate={endMaxDate}
              minDate={endMinDate}
              locale={ko}
              customInput={<DateField />}
            />
          </div>
        </div>

        <div className="hourly-toolbar__divider" aria-hidden="true" />

        <div className="hourly-toolbar__section">
          <span className="hourly-toolbar__label">표시 범위</span>
          <div className="hourly-pill-group">
            <button
              type="button"
              className={`hourly-pill ${tempViewMode === 'individual' ? 'active' : ''}`}
              onClick={() => setTempViewMode('individual')}
            >
              개별
            </button>
            <button
              type="button"
              className={`hourly-pill ${tempViewMode === 'total' ? 'active' : ''}`}
              onClick={() => setTempViewMode('total')}
            >
              전체
            </button>
          </div>
        </div>

        <div className="hourly-toolbar__section">
          <span className="hourly-toolbar__label">표시 형식</span>
          <div className="hourly-pill-group">
            <button
              type="button"
              className={`hourly-pill ${tempDisplayType === 'table' ? 'active' : ''}`}
              onClick={() => setTempDisplayType('table')}
            >
              표
            </button>
            <button
              type="button"
              className={`hourly-pill ${tempDisplayType === 'chart' ? 'active' : ''}`}
              onClick={() => setTempDisplayType('chart')}
            >
              그래프
            </button>
          </div>
        </div>

        <div className="hourly-toolbar__actions">
          {hasCsv && (
            <CSVLink
              data={csvData}
              headers={csvHeaders}
              filename={csvFilename}
              className="hourly-toolbar__csv"
            >
              CSV 다운로드
            </CSVLink>
          )}

          <button
            type="button"
            className="hourly-toolbar__search"
            onClick={handleSearch}
            disabled={isLoading}
          >
            <SearchIcon />
            {isLoading ? '조회 중...' : '조회'}
          </button>
        </div>
      </div>

      {rangeHint && <p className="hourly-range-hint">{rangeHint}</p>}
    </div>
  );
};

export default ReportToolbar;
